import { Alert, Descriptions, Tag, Typography, Space } from 'antd';
import {
  humaniseEnum, isEcommercePublishable, isGoodsOrServiceComplete
} from '../../studioConstants';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────
// Step 7 — Review & Save.
//
// Pure presentational: takes the live Form values and renders a
// summary. The actual save lives on the parent's footer button so the
// review screen stays a "what will happen if I click Save?" surface.
// ─────────────────────────────────────────────────────────────────

function fmtBool(v) {
  if (v === true)  return <Tag color="blue">Yes</Tag>;
  if (v === false) return <Tag>No</Tag>;
  return <span style={{ color: '#94A3B8' }}>—</span>;
}

function fmtVal(v) {
  if (v === null || v === undefined || v === '') return <span style={{ color: '#94A3B8' }}>—</span>;
  return String(v);
}

function fmtMoney(v) {
  if (v === null || v === undefined || v === '') return <span style={{ color: '#94A3B8' }}>—</span>;
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n.toFixed(2);
}

function fmtEnum(v) {
  if (!v) return <span style={{ color: '#94A3B8' }}>—</span>;
  return humaniseEnum(v);
}

export default function ReviewSaveStep({
  values, enabledSections, imageCount, mode, itemId
}) {
  const v = values || {};
  const warnings = [];

  if (!v.itemName) warnings.push('Item name is required.');
  if (!v.itemCode) warnings.push('Item code is required.');
  if (!isGoodsOrServiceComplete(v)) {
    warnings.push('At least one of Goods or Service must be on.');
  }
  if (v.taxabilityType === 'service' || v.taxabilityType === 'rcm') {
    warnings.push('Taxability "service" / "rcm" is not allowed on Item Master.');
  }
  if (enabledSections && enabledSections.ecommerce && v.publishStatus === 'published'
      && !isEcommercePublishable(v)) {
    warnings.push('Ecommerce profile is set to published but is missing slug, visibility or visible-online flag.');
  }

  return (
    <div>
      <h2 className="erp-studio-section-title">Review & save</h2>
      <p className="erp-studio-section-help">
        Confirm everything looks right, then click Save Item at the bottom. The system will
        create the core record first, then save any enabled technical specs and ecommerce profile.
      </p>

      {warnings.length > 0 ? (
        <Alert
          type="warning" showIcon
          style={{ marginBottom: 16 }}
          message={`${warnings.length} validation issue${warnings.length === 1 ? '' : 's'}`}
          description={<ul style={{ margin: 0, paddingLeft: 18 }}>{warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>}
        />
      ) : null}

      <Descriptions
        size="small" bordered column={{ xs: 1, sm: 2, md: 3 }}
        title="Core item"
        labelStyle={{ width: 140, color: '#475569', fontSize: 12 }}
        contentStyle={{ fontSize: 12.5 }}
      >
        <Descriptions.Item label="Item code">{fmtVal(v.itemCode)}</Descriptions.Item>
        <Descriptions.Item label="Item name" span={2}>{fmtVal(v.itemName)}</Descriptions.Item>
        <Descriptions.Item label="Item type">{fmtEnum(v.itemType)}</Descriptions.Item>
        <Descriptions.Item label="Status">{fmtEnum(v.status)}</Descriptions.Item>
        <Descriptions.Item label="SKU">{fmtVal(v.skucode)}</Descriptions.Item>
        <Descriptions.Item label="Barcode">{fmtVal(v.barcode)}</Descriptions.Item>
        <Descriptions.Item label="HSN/SAC">{fmtVal(v.hsncode)}</Descriptions.Item>
        <Descriptions.Item label="GST rate %">{fmtMoney(v.gstRatePercent)}</Descriptions.Item>
        <Descriptions.Item label="Taxability">{fmtEnum(v.taxabilityType)}</Descriptions.Item>
        <Descriptions.Item label="Cess applicable">{fmtBool(v.cessApplicable)}</Descriptions.Item>
        <Descriptions.Item label="Cess rate %">{fmtMoney(v.cessRate)}</Descriptions.Item>
        <Descriptions.Item label="UQC">{fmtVal(v.defaultUqcCode)}</Descriptions.Item>
      </Descriptions>

      <div style={{ marginTop: 12 }}>
        <Descriptions
          size="small" bordered column={{ xs: 1, sm: 2, md: 4 }}
          title="Flags"
          labelStyle={{ width: 140, color: '#475569', fontSize: 12 }}
          contentStyle={{ fontSize: 12.5 }}
        >
          <Descriptions.Item label="Goods">{fmtBool(v.isGoods)}</Descriptions.Item>
          <Descriptions.Item label="Service">{fmtBool(v.isService)}</Descriptions.Item>
          <Descriptions.Item label="Stock">{fmtBool(v.isStockItem)}</Descriptions.Item>
          <Descriptions.Item label="Purchasable">{fmtBool(v.isPurchasable)}</Descriptions.Item>
          <Descriptions.Item label="Sellable">{fmtBool(v.isSellable)}</Descriptions.Item>
          <Descriptions.Item label="Manufacturable">{fmtBool(v.isManufacturable)}</Descriptions.Item>
          <Descriptions.Item label="Consumable">{fmtBool(v.isConsumable)}</Descriptions.Item>
          <Descriptions.Item label="Jobwork item">{fmtBool(v.isJobworkItem)}</Descriptions.Item>
        </Descriptions>
      </div>

      <div style={{ marginTop: 12 }}>
        <Descriptions
          size="small" bordered column={{ xs: 1, sm: 2, md: 3 }}
          title="Pricing"
          labelStyle={{ width: 140, color: '#475569', fontSize: 12 }}
          contentStyle={{ fontSize: 12.5 }}
        >
          <Descriptions.Item label="Purchase rate">{fmtMoney(v.purchaseRate)}</Descriptions.Item>
          <Descriptions.Item label="Sales rate">{fmtMoney(v.salesRate)}</Descriptions.Item>
          <Descriptions.Item label="MRP">{fmtMoney(v.mrp)}</Descriptions.Item>
          <Descriptions.Item label="Pricing method">{fmtEnum(v.pricingMethod)}</Descriptions.Item>
          <Descriptions.Item label="Min order qty">{fmtVal(v.minOrderQty)}</Descriptions.Item>
          <Descriptions.Item label="Lead time (d)">{fmtVal(v.leadTimeDays)}</Descriptions.Item>
        </Descriptions>
      </div>

      <div style={{ marginTop: 12 }}>
        <Text strong style={{ fontSize: 13 }}>Enabled sections</Text>
        <div style={{ marginTop: 6 }}>
          <Space wrap size={[6, 6]}>
            {enabledSections && enabledSections.glass    ? <Tag color="processing">Glass details</Tag>    : null}
            {enabledSections && enabledSections.profile  ? <Tag color="processing">Profile details</Tag>  : null}
            {enabledSections && enabledSections.hardware ? <Tag color="processing">Hardware details</Tag> : null}
            {enabledSections && enabledSections.jobwork  ? <Tag color="processing">Jobwork details</Tag>  : null}
            {enabledSections && enabledSections.ecommerce ? <Tag color="purple">Ecommerce profile</Tag>   : null}
            {(typeof imageCount === 'number' && imageCount > 0)
              ? <Tag color="gold">{imageCount} image{imageCount === 1 ? '' : 's'}</Tag>
              : <Tag>No images yet</Tag>}
            {(!enabledSections || (!enabledSections.glass && !enabledSections.profile
              && !enabledSections.hardware && !enabledSections.jobwork && !enabledSections.ecommerce))
              ? <Tag>Core only</Tag>
              : null}
          </Space>
        </div>
      </div>

      <div style={{ marginTop: 16, fontSize: 12, color: '#64748B' }}>
        Mode: <strong>{mode === 'edit' ? 'Editing existing item' : 'Creating new item'}</strong>
        {itemId ? <> · ID: <span style={{ fontFamily: 'monospace' }}>{itemId}</span></> : null}
      </div>
    </div>
  );
}
